/**
 * Image Generation Service
 * Handles all image generation API calls for story illustrations
 */
import { authFetch } from '../lib/api.js';

/**
 * Generate a single image using Gemini
 * @param {string} prompt - The image generation prompt
 * @param {object} options - Additional options
 * @param {string} authToken - Clerk auth token
 * @returns {Promise<{success: boolean, imageUrl?: string, error?: string}>}
 */
export const generateImage = async (prompt, options = {}, authToken = null) => {
  try {
    const response = await authFetch('/api/generate-image', {
      method: 'POST',
      body: JSON.stringify({
        prompt,
        aspectRatio: options.aspectRatio || '16:9',
        numberOfImages: options.numberOfImages || 1,
        imageModel: options.imageModel || 'gemini-3.1-flash-lite-image',
        referenceAvatars: options.referenceAvatars || [],
        storyId: options.storyId || null,
      }),
    }, authToken);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      imageUrl: data.imageUrl,
      images: data.images,
    };
  } catch (error) {
    console.error('[imageService] generateImage error:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Generate images for all pages of a story
 * @param {Array} pages - Array of page objects with text and sceneDescription
 * @param {object} context - Story context (characters, setting, style)
 * @param {function} onProgress - Progress callback (pageIndex, total, status)
 * @param {string} authToken - Clerk auth token
 * @returns {Promise<Array>}
 */
export const generateStoryImages = async (pages, context = {}, onProgress = null, authToken = null) => {
  try {
    // Notify start
    if (onProgress) {
      onProgress(0, pages.length, 'starting');
    }

    // Keep each serverless invocation short enough for Vercel's runtime
    // window. The endpoint still parallelizes within a batch, while this
    // client-side batching avoids timing out on a full six-to-ten-page book.
    const batchSize = 2;
    const images = [];
    for (let start = 0; start < pages.length; start += batchSize) {
      const batch = pages.slice(start, start + batchSize);
      const response = await authFetch('/api/generate-story-images', {
        method: 'POST',
        body: JSON.stringify({
          pages: batch,
          characters: context.characters || [],
          setting: context.setting || '',
          style: context.style || '',
          imageModel: context.imageModel || 'gemini-3.1-flash-lite-image',
          storyId: context.storyId || null,
        }),
      }, authToken);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error: ${response.status}`);
      }

      const data = await response.json();
      const batchImages = (data.images || []).map((image) => ({
        ...image,
        // The API receives each batch as a new zero-based array. Restore the
        // original page index before handing results back to the wizard.
        pageIndex: Number.isInteger(image.pageIndex) ? image.pageIndex + start : image.pageIndex,
      }));
      images.push(...batchImages);
      if (onProgress) {
        onProgress(
          Math.min(start + batch.length, pages.length),
          pages.length,
          'progress',
          batchImages
        );
      }
    }

    if (onProgress) onProgress(pages.length, pages.length, 'complete');
    return images;
  } catch (error) {
    console.error('[imageService] generateStoryImages error:', error);
    throw error;
  }
};

/**
 * Generate a cover image for a story
 * @param {string} title - Story title
 * @param {object} mainCharacter - Main character object
 * @param {string} setting - Story setting
 * @param {Array} themes - Story themes
 * @param {string} authToken - Clerk auth token
 * @returns {Promise<{success: boolean, imageUrl?: string, error?: string}>}
 */
export const generateCoverImage = async (title, mainCharacter, setting, themes = [], authToken = null, imageModel = 'gemini-3.1-flash-lite-image', storyId = null) => {
  const characterDescription = mainCharacter
    ? `${mainCharacter.name}${mainCharacter.appearance ? `, ${mainCharacter.appearance}` : ''}`
    : 'a child';

  const themeText = themes.length > 0 ? themes.slice(0, 2).join(' and ') : 'adventure';

  const prompt = `Artwork-only book cover for a children's story titled "${title}".
    Feature ${characterDescription} in a ${setting || 'magical'} setting.
    Theme: ${themeText}.
    Make it colorful, inviting, and perfect for a children's book cover. Depict each named character exactly once; do not duplicate, mirror, clone, or repeat a character.
    Do not render any title, author name, words, letters, numbers, logos, signs, labels, or typography in the image. The app will add the title and author as a separate readable text layer.`;

  return generateImage(prompt, {
    aspectRatio: '3:4',
    imageModel,
    referenceAvatars: mainCharacter?.avatar ? [mainCharacter] : [],
    storyId,
  }, authToken);
};

/**
 * Generate scene description from story text using AI
 * @param {string} pageText - The text content of the page
 * @param {object} context - Story context
 * @returns {Promise<string>} Scene description for image generation
 */
export const generateSceneDescription = async (pageText, context = {}) => {
  try {
    const response = await fetch('/api/generate-scene-description', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pageText,
        characters: context.characters || [],
        setting: context.setting || '',
      }),
    });

    if (!response.ok) {
      // Fallback: extract key visual elements from text
      return extractVisualElements(pageText);
    }

    const data = await response.json();
    return data.sceneDescription || extractVisualElements(pageText);
  } catch (error) {
    console.error('[imageService] generateSceneDescription error:', error);
    return extractVisualElements(pageText);
  }
};

/**
 * Extract visual elements from text (fallback)
 * @param {string} text - Story text
 * @returns {string} Simple scene description
 */
const extractVisualElements = (text) => {
  // Simple extraction of nouns and adjectives for basic scene
  const words = text.split(/\s+/).slice(0, 30).join(' ');
  return `Scene showing: ${words}`;
};

/**
 * Parse story content into pages with scene descriptions
 * @param {string} storyContent - Full story text
 * @param {number} pagesCount - Target number of pages (default: auto)
 * @returns {Array} Array of page objects
 */
export const parseStoryIntoPages = (storyContent, pagesCount = null) => {
  if (!storyContent) return [];

  // Split by double newlines (paragraphs)
  const paragraphs = storyContent
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(p => p.length > 0);

  if (paragraphs.length === 0) return [];

  // Determine pages count
  const targetPages = pagesCount || Math.min(Math.max(Math.ceil(paragraphs.length / 2), 4), 10);

  // Group paragraphs into pages
  const pages = [];
  const paragraphsPerPage = Math.ceil(paragraphs.length / targetPages);

  for (let i = 0; i < paragraphs.length; i += paragraphsPerPage) {
    const pageParas = paragraphs.slice(i, i + paragraphsPerPage);
    const pageText = pageParas.join('\n\n');

    pages.push({
      pageNumber: pages.length + 1,
      text: pageText,
      sceneDescription: extractVisualElements(pageText),
      imageUrl: null,
      imageStatus: 'pending',
    });
  }

  return pages;
};

export default {
  generateImage,
  generateStoryImages,
  generateCoverImage,
  generateSceneDescription,
  parseStoryIntoPages,
};
